import React, { useState } from "react";
import { AnswerIcon } from "./AnswerIcon";
import ReCAPTCHA from "react-google-recaptcha";
import { verifyRecaptcha } from "../../api";
import { Spinner, ISpinnerStyles } from "@fluentui/react";

interface Props {
    error: string;
    onRetry: () => void;
    isProfanity?: boolean;
    isMessageLimit?: boolean;
    isConversationLimit?: boolean;
    isExpired?: boolean;
}

export const AnswerError = ({ error, onRetry, isProfanity, isMessageLimit, isConversationLimit, isExpired }: Props) => {
    const [reCaptchaToken, setReCaptchaToken] = useState("");

    // reCAPTCHA settings
    const handleRecaptcha = (value: any) => {
        setReCaptchaToken(value);
    };
    const [loading, setLoading] = useState(false);
    const siteKey = import.meta.env.VITE_SITE_KEY;

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setLoading(true); // Show a loading indicator

        try {
            const response = await verifyRecaptcha(reCaptchaToken);
            if (!response) {
                throw Error("No response body");
            }
            if ("token" in response) {
                const token = response.token;
                sessionStorage.setItem("token", token);
                onRetry();
            } else {
                throw Error("Invalid response");
            }
        } catch (error) {
            console.error("reCAPTCHA verification failed", error);
        } finally {
            setLoading(false); // Hide the loading indicator
        }
    };

    const spinnerStyles: ISpinnerStyles = {
        circle: {
            borderColor: "#FFFFFF #FFFFFF30 #FFFFFF30",
            height: "20px",
            width: "20px"
        }
    };

    return (
        <div className="w-full">
            <div className="py-[18px] px-4 text-base m-auto ">
                <div className="mx-auto flex flex-1 text-base gap-4 lg:gap-6 lg:max-w-3xl">
                    <div className="flex-shrink-0 flex flex-col relative items-end">
                        <div>
                            <div className="pt-0">
                                <div className="relative p-1 flex items-center justify-center h-9 w-9 lg:h-16 lg:w-16">
                                    <AnswerIcon isError={true} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="relative flex w-full min-w-0 flex-col rounded-xl bg-j-white px-6 py-8 text-j-dark shadow-[0px_8px_16px_rgba(0,0,0,0.14),0px_0px_2px_rgba(0,0,0,0.12)]">
                        <div className="flex-col gap-1 lg:gap-3">
                            <div className="flex flex-grow flex-col max-w-full">
                                <div className="flex flex-col gap-1 lg:gap-3">
                                    <div className="flex flex-grow flex-col max-w-full">
                                        <div className="min-h-[20px] text-message flex flex-col whitespace-pre-wrap break-words w-full items-end overflow-x-auto gap-2">
                                            <div className="flex w-full flex-col gap-1 first:pt-[3px]">
                                                <div className="w-full break-words">
                                                    {isProfanity || isMessageLimit || isConversationLimit || isExpired
                                                        ? error.replace(/^Error:\s*/, "")
                                                        : error}
                                                </div>
                                            </div>
                                        </div>
                                        {!(isProfanity || isMessageLimit || isConversationLimit || isExpired) && (
                                            <button
                                                className="mt-4 flex w-32 justify-center rounded-lg bg-j-red px-3 py-1.5 text-sm font-bold leading-6 text-j-white hover:bg-j-red-hover"
                                                onClick={onRetry}
                                            >
                                                <span>Retry</span>
                                            </button>
                                        )}
                                        {isExpired && (
                                            <>
                                                <div className="mt-4 flex py-2">
                                                    <ReCAPTCHA onChange={handleRecaptcha} sitekey={siteKey} />
                                                </div>
                                                {reCaptchaToken && (
                                                    <button
                                                        type="button"
                                                        onClick={handleSubmit}
                                                        className="mt-4 flex w-32 justify-center rounded-lg bg-j-red px-3 py-1.5 text-sm font-bold leading-6 text-j-white hover:bg-j-red-hover"
                                                    >
                                                        {loading ? (
                                                            <Spinner style={{ display: "flex", verticalAlign: "middle" }} styles={spinnerStyles} />
                                                        ) : (
                                                            "Retry"
                                                        )}
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
